import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import {
  FaFilter, FaTimes, FaChevronLeft, FaChevronRight,
  FaSortUp, FaSortDown, FaSort, FaSearch, FaTable, FaLayerGroup,
  FaChevronDown, FaInfoCircle, FaUndo, FaFileExcel
} from 'react-icons/fa'
import { Treemap, ResponsiveContainer } from 'recharts'
import AtlasPartDetailPanel from './AtlasPartDetailPanel'
import { exportBatchXlsx } from './atlasExportXlsx'

const API_BASE = process.env.REACT_APP_BE_MAIN_URL

const PROCESS_COLORS = {
  'machining': '#1e5045',
  'casting': '#f26522',
  'sheet_metal': '#60a5fa',
  'assembly': '#a78bfa',
  'weldment': '#fb923c',
  'other': '#94a3b8',
}
const MATERIAL_COLORS = {
  'Aluminum': '#60a5fa',
  'Steel': '#f87171',
  'Titanium': '#a78bfa',
  'Stainless Steel': '#34d399',
  'Copper': '#fb923c',
}
const DEFAULT_COLOR = '#94a3b8'

const CONF_STYLES = {
  high: 'bg-green-100 text-green-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-red-100 text-red-800',
}

function TreemapCell({ x, y, width, height, name, value, depth, root }) {
  if (depth === 0 || !name) return null
  // Color by parent process name
  const parentName = name.includes(' > ') ? name.split(' > ')[0] : name
  const color = PROCESS_COLORS[parentName.toLowerCase().replace(' ', '_')] || MATERIAL_COLORS[parentName] || DEFAULT_COLOR
  if (width < 30 || height < 20) return (
    <g><rect x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.7} stroke="#fff" strokeWidth={1} /></g>
  )
  const label = name.includes(' > ') ? name.split(' > ')[1] : name
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.8} stroke="#fff" strokeWidth={2} rx={4} />
      {height > 36 && (
        <>
          <text x={x + width / 2} y={y + height / 2 - 6} textAnchor="middle" fill="#fff" fontSize={Math.min(13, width / 8)} fontWeight={600} style={{ pointerEvents: 'none' }}>
            {label.length > 16 ? label.slice(0, 16) + '…' : label}
          </text>
          <text x={x + width / 2} y={y + height / 2 + 10} textAnchor="middle" fill="#fff" fontSize={10} style={{ pointerEvents: 'none' }}>
            ({value})
          </text>
        </>
      )}
    </g>
  )
}

function SortTh({ col, label, sortCol, sortDir, onSort }) {
  const active = sortCol === col
  return (
    <th
      className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
      onClick={() => onSort(col)}
    >
      <span className="flex items-center gap-1">
        {label}
        {active
          ? sortDir === 'asc' ? <FaSortUp size={10} className="text-[#1e5045]" /> : <FaSortDown size={10} className="text-[#1e5045]" />
          : <FaSort size={10} className="text-gray-300" />
        }
      </span>
    </th>
  )
}

function KPICard({ label, value, sub, accent = 'text-[#1e5045]' }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 shadow-sm">
      <div className={`text-3xl font-bold ${accent}`}>{value}</div>
      <div className="text-sm font-medium text-gray-700 mt-0.5">{label}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  )
}

export default function EngineeringAtlasTab({ spacePath, folderName, onResetAll }) {
  const [data, setData] = useState([])
  const [facetCounts, setFacetCounts] = useState({})
  const [treemapRaw, setTreemapRaw] = useState({})
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [selectedFileId, setSelectedFileId] = useState(null)
  const [checkedIds, setCheckedIds] = useState(new Set())
  const [batchExporting, setBatchExporting] = useState(false)
  const [search, setSearch] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('search') || ''
  })
  const [sortCol, setSortCol] = useState('filename')
  const [sortDir, setSortDir] = useState('asc')
  const PAGE_SIZE = 50

  // Multi-select filters
  const [filters, setFilters] = useState({
    material: [],
    process: [],
    size: [],
    tolerance: [],
    complexity: [],
    axis: [],
    setup: [],
    bend: [],
    stock_form: [],
    btf: [],
    cert: [],
    confidence: [],
    shape: [],
    shape_sub: [],
    drawing_type: [],
    coating: [],
    condition_temper: [],
    machining_route: [],
    secondary_processes: [],
    weight: [],
    gdt: [],
    dim_density: [],
    units: [],
    released_status: [],
  })

  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPage(1)
  }
  const toggleFilterValue = (key, val) => {
    setFilters(prev => {
      const arr = prev[key]
      return { ...prev, [key]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] }
    })
    setPage(1)
  }

  const activeCount = Object.values(filters).reduce((sum, arr) => sum + arr.length, 0)
  const clearAll = () => {
    setFilters(prev => Object.fromEntries(Object.keys(prev).map(k => [k, []])))
    setPage(1)
  }

  // Multi-select for batch export
  const toggleChecked = (fileId) => {
    setCheckedIds(prev => {
      const next = new Set(prev)
      if (next.has(fileId)) next.delete(fileId)
      else next.add(fileId)
      return next
    })
  }
  const allPageChecked = data.length > 0 && data.every(d => checkedIds.has(d.file_id))
  const toggleAllPage = () => {
    setCheckedIds(prev => {
      const next = new Set(prev)
      if (allPageChecked) {
        data.forEach(d => next.delete(d.file_id))
      } else {
        data.forEach(d => next.add(d.file_id))
      }
      return next
    })
  }

  const handleBatchExport = async () => {
    if (checkedIds.size === 0) return
    setBatchExporting(true)
    const token = sessionStorage.getItem('userToken')
    try {
      // Fetch full detail for each checked part (parallel, batched)
      const ids = [...checkedIds]
      const results = await Promise.all(
        ids.map(id =>
          axios.get(`${API_BASE}/ocr/network/atlas-part-detail/${id}/`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(res => res.data.part).catch(() => null)
        )
      )
      const parts = results.filter(Boolean)
      if (parts.length > 0) {
        exportBatchXlsx(parts)
      }
    } finally {
      setBatchExporting(false)
    }
  }

  // Families view state
  const [viewMode, setViewMode] = useState('table') // 'table' | 'families'
  const [familiesData, setFamiliesData] = useState([])
  const [famTotal, setFamTotal] = useState(0)
  const [famPage, setFamPage] = useState(1)
  const [famLoading, setFamLoading] = useState(false)
  const [famAxes, setFamAxes] = useState(['material_family', 'envelope_class'])
  const [famSortCol, setFamSortCol] = useState('part_count')
  const [famSortDir, setFamSortDir] = useState('desc')
  const [expandedFam, setExpandedFam] = useState(new Set())
  const [processFamily, setProcessFamily] = useState('all')
  const [pfCounts, setPfCounts] = useState({})
  const [totalParts, setTotalParts] = useState(0)
  const FAM_PAGE_SIZE = 50

  useEffect(() => {
    if (!spacePath || !folderName) return
    setLoading(true)
    const token = sessionStorage.getItem("userToken")
    const params = {
      space_path: spacePath,
      folder_name: folderName,
      page,
      page_size: PAGE_SIZE,
      search,
      sort: sortCol,
      order: sortDir,
    }
    // Append active filters as comma-separated
    Object.entries(filters).forEach(([key, vals]) => {
      if (vals.length > 0) params[key] = vals.join(',')
    })
    axios.get(`${API_BASE}/ocr/network/atlas-browse/`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    }).then(res => {
      setData(res.data.results || [])
      setTotal(res.data.total || 0)
      if (res.data.facet_counts) setFacetCounts(res.data.facet_counts)
      if (res.data.treemap) setTreemapRaw(res.data.treemap)
    }).catch(() => {
      setData([])
    }).finally(() => setLoading(false))
  }, [spacePath, folderName, page, search, sortCol, sortDir, filters])

  // Fetch families data
  useEffect(() => {
    if (!spacePath || !folderName || viewMode !== 'families') return
    setFamLoading(true)
    const token = sessionStorage.getItem("userToken")
    axios.get(`${API_BASE}/ocr/network/atlas-families/`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        space_path: spacePath,
        folder_name: folderName,
        axes: famAxes.join(','),
        sort: famSortCol,
        order: famSortDir,
        page: famPage,
        page_size: FAM_PAGE_SIZE,
        process_family: processFamily,
      }
    }).then(res => {
      setFamiliesData(res.data.families || [])
      setFamTotal(res.data.total || 0)
      if (res.data.process_family_counts) setPfCounts(res.data.process_family_counts)
      if (res.data.total_parts != null) setTotalParts(res.data.total_parts)
      if (res.data.treemap) setTreemapRaw(res.data.treemap)
    }).catch(() => {
      setFamiliesData([])
    }).finally(() => setFamLoading(false))
  }, [spacePath, folderName, viewMode, famAxes, famSortCol, famSortDir, famPage, processFamily])

  const totalPages = Math.ceil(total / PAGE_SIZE)
  const famTotalPages = Math.ceil(famTotal / FAM_PAGE_SIZE)

  const handleSort = (col) => {
    if (col === sortCol) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  const handleFamSort = (col) => {
    if (col === famSortCol) setFamSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setFamSortCol(col); setFamSortDir('desc') }
  }

  // KPIs
  const highPct = data.length ? Math.round(data.filter(d => d.confidence === 'high').length / data.length * 100) : 0
  const processCounts = data.reduce((a, d) => { a[d.primary_process] = (a[d.primary_process] || 0) + 1; return a }, {})
  const topProcess = Object.entries(processCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '—'
  const matCounts = data.reduce((a, d) => { a[d.material_family] = (a[d.material_family] || 0) + 1; return a }, {})
  const topMat = Object.entries(matCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '—'

  // Treemap: built from server aggregation, adapts to selected axes
  const treemapData = useMemo(() => {
    if (!treemapRaw || Object.keys(treemapRaw).length === 0) return []
    // Check if nested (2 axes: parent → {child: count}) or flat (1 axis: {val: count})
    const firstVal = Object.values(treemapRaw)[0]
    if (typeof firstVal === 'object' && firstVal !== null) {
      // Nested: parent → child → count
      return Object.entries(treemapRaw).map(([parent, children]) => ({
        name: parent,
        children: Object.entries(children).map(([child, count]) => ({
          name: `${parent} > ${child}`,
          size: count,
        })),
      }))
    }
    // Flat: value → count
    return [{ name: 'All', children: Object.entries(treemapRaw).map(([val, count]) => ({ name: val, size: count })) }]
  }, [treemapRaw])

  if (!spacePath || !folderName) {
    return (
      <div className="text-center py-20 text-gray-400">
        <p className="text-lg">Select a space and folder to browse</p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* KPI bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Total Parts" value={total} sub="in this folder" />
        <KPICard label="High Confidence" value={`${highPct}%`} sub="of visible page" accent="text-green-600" />
        <KPICard label="Top Process" value={topProcess} sub="most common" accent="text-[#f26522]" />
        <KPICard label="Top Material" value={topMat} sub="most common" accent="text-blue-600" />
      </div>

      <div className="flex gap-5">
        {/* Facet rail */}
        <div className="w-56 flex-shrink-0 space-y-3">
          <FilterRail
            facetCounts={facetCounts}
            filters={filters}
            toggleFilterValue={toggleFilterValue}
            activeCount={activeCount}
            clearAll={clearAll}
            onResetAll={onResetAll}
          />
        </div>

        {/* Right panel */}
        <div className="flex-1 space-y-5 min-w-0">
          {/* Treemap */}
          {treemapData.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold text-gray-700">
                  {viewMode === 'families'
                    ? `Parts by ${famAxes.slice(0, 2).map(a => a.replace(/_/g, ' ').replace('family', '').replace('class', '').trim()).join(' & ')}`
                    : 'Parts by Process & Material'}
                </span>
                <div className="flex flex-wrap gap-2 ml-auto">
                  {Object.keys(treemapRaw).slice(0, 6).map(key => (
                    <div key={key} className="flex items-center gap-1 text-xs text-gray-500">
                      <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: PROCESS_COLORS[key.toLowerCase().replace(' ', '_')] || MATERIAL_COLORS[key] || DEFAULT_COLOR }} />
                      {key.replace(/_/g, ' ')}
                    </div>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <Treemap data={treemapData} dataKey="size" aspectRatio={4 / 3} content={<TreemapCell />} />
              </ResponsiveContainer>
            </div>
          )}

          {/* Search + View toggle */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
              <input
                type="text"
                placeholder="Search parts, materials, drawings..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1) }}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1e5045]"
              />
            </div>
            <span className="text-xs text-gray-400">{total} results</span>
            {checkedIds.size > 0 && (
              <button
                onClick={handleBatchExport}
                disabled={batchExporting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
              >
                <FaFileExcel size={11} />
                {batchExporting ? 'Exporting...' : `Export ${checkedIds.size} to XLSX`}
              </button>
            )}
            {checkedIds.size > 0 && (
              <button
                onClick={() => setCheckedIds(new Set())}
                className="text-[10px] text-gray-400 hover:text-gray-600 underline"
              >
                Clear selection
              </button>
            )}
            <div className="ml-auto flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition ${viewMode === 'table' ? 'bg-white text-[#1e5045] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <FaTable size={10} /> Table
              </button>
              <button
                onClick={() => setViewMode('families')}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition ${viewMode === 'families' ? 'bg-white text-[#1e5045] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <FaLayerGroup size={10} /> Families
              </button>
            </div>
          </div>

          {/* Families view */}
          {viewMode === 'families' && (
            <FamiliesPanel
              familiesData={familiesData}
              famTotal={famTotal}
              famPage={famPage}
              setFamPage={setFamPage}
              famTotalPages={famTotalPages}
              famLoading={famLoading}
              famAxes={famAxes}
              setFamAxes={setFamAxes}
              famSortCol={famSortCol}
              handleFamSort={handleFamSort}
              famSortDir={famSortDir}
              expandedFam={expandedFam}
              setExpandedFam={setExpandedFam}
              processFamily={processFamily}
              setProcessFamily={setProcessFamily}
              pfCounts={pfCounts}
              totalParts={totalParts}
            />
          )}

          {/* Parts table */}
          {viewMode === 'table' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {loading ? (
              <div className="py-12 text-center text-gray-400 text-sm">Loading...</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="px-2 py-2 w-8">
                          <input
                            type="checkbox"
                            checked={allPageChecked}
                            onChange={toggleAllPage}
                            className="rounded border-gray-300 text-[#1e5045] focus:ring-[#1e5045] w-3.5 h-3.5"
                          />
                        </th>
                        <SortTh col="filename" label="Drawing" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortTh col="part_number" label="Part #" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortTh col="part_name" label="Description" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortTh col="drawing_type" label="Type" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortTh col="revision" label="Rev" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortTh col="material_normalized" label="Material" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortTh col="envelope_class" label="Envelope" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortTh col="primary_process" label="Process" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortTh col="machining_route" label="Route" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortTh col="tightest_linear_mm" label="Tightest Tol" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortTh col="confidence" label="Confidence" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                      </tr>
                    </thead>
                    <tbody>
                      {data.map(d => (
                        <tr key={d.file_id || d.filename} onClick={() => setSelectedFileId(d.file_id)} className={`border-b border-gray-50 hover:bg-gray-50 cursor-pointer ${checkedIds.has(d.file_id) ? 'bg-green-50/40' : ''}`}>
                          <td className="px-2 py-2" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={checkedIds.has(d.file_id)}
                              onChange={() => toggleChecked(d.file_id)}
                              className="rounded border-gray-300 text-[#1e5045] focus:ring-[#1e5045] w-3.5 h-3.5"
                            />
                          </td>
                          <td className="px-3 py-2 font-mono text-gray-600 text-[11px] truncate max-w-[200px]">{d.filename}</td>
                          <td className="px-3 py-2 font-mono font-medium text-gray-800">{d.part_number || '—'}</td>
                          <td className="px-3 py-2 text-gray-600 text-[11px] truncate max-w-[200px]">{d.part_name || '—'}</td>
                          <td className="px-3 py-2 text-gray-500 text-[10px] capitalize">{d.drawing_type || '—'}</td>
                          <td className="px-3 py-2 font-mono text-gray-600 text-[11px]">{d.revision || '—'}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: MATERIAL_COLORS[d.material_family] || DEFAULT_COLOR }} />
                              {d.material_normalized || d.material_family || '—'}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            {d.envelope_class ? <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-[11px] font-medium">{d.envelope_class}</span> : '—'}
                          </td>
                          <td className="px-3 py-2 text-gray-600">{d.primary_process || '—'}</td>
                          <td className="px-3 py-2 text-gray-500 text-[10px]">{d.machining_route || '—'}</td>
                          <td className="px-3 py-2 font-mono">
                            {d.tightest_linear_mm != null ? `±${d.tightest_linear_mm.toFixed(3)} mm` : '—'}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${CONF_STYLES[d.confidence] || 'bg-gray-100 text-gray-600'}`}>
                              {d.confidence || '—'}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {data.length === 0 && !loading && (
                        <tr>
                          <td colSpan={12} className="px-4 py-8 text-center text-gray-400">
                            {total === 0 ? 'No drawings with atlas data in this folder.' : 'No results match filters.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg disabled:opacity-40 hover:bg-gray-200"
                    >
                      <FaChevronLeft size={10} /> Prev
                    </button>
                    <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg disabled:opacity-40 hover:bg-gray-200"
                    >
                      Next <FaChevronRight size={10} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Part Detail Slide-Out */}
      {selectedFileId && (
        <>
          <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setSelectedFileId(null)} />
          <AtlasPartDetailPanel
            fileId={selectedFileId}
            onClose={() => setSelectedFileId(null)}
            onNavigate={(newId) => setSelectedFileId(newId)}
          />
        </>
      )}
    </div>
  )
}

const PROCESS_FAMILIES = [
  { key: 'all', label: 'All' },
  { key: 'machining', label: 'Machining' },
  { key: 'sheet_metal', label: 'Sheet Metal' },
  { key: 'other', label: 'Other' },
]

// Axes per process family — matches FamiliesView in Supply Chain Analytics
const MACHINING_AXES = [
  { key: 'material_family', label: 'Material' },
  { key: 'envelope_class', label: 'Size' },
  { key: 'tolerance_band', label: 'Tolerance' },
  { key: 'axis_tier', label: 'Axis' },
  { key: 'complexity_tier', label: 'Complexity' },
]
const SHEET_METAL_AXES = [
  { key: 'material_family', label: 'Material' },
  { key: 'envelope_class', label: 'Size' },
  { key: 'gauge_band', label: 'Gauge' },
  { key: 'bend_class', label: 'Bends' },
  { key: 'complexity_tier', label: 'Complexity' },
]
const OTHER_AXES = [
  { key: 'primary_process', label: 'Process' },
  { key: 'material_family', label: 'Material' },
  { key: 'envelope_class', label: 'Size' },
  { key: 'tolerance_band', label: 'Tolerance' },
  { key: 'complexity_tier', label: 'Complexity' },
]
const ALL_AXES = [
  { key: 'primary_process', label: 'Process' },
  { key: 'material_family', label: 'Material' },
  { key: 'envelope_class', label: 'Size' },
  { key: 'tolerance_band', label: 'Tolerance' },
  { key: 'complexity_tier', label: 'Complexity' },
]

function getAxesForFamily(pf) {
  switch (pf) {
    case 'machining': return MACHINING_AXES
    case 'sheet_metal': return SHEET_METAL_AXES
    case 'other': return OTHER_AXES
    default: return ALL_AXES
  }
}

function FamiliesPanel({ familiesData, famTotal, famPage, setFamPage, famTotalPages, famLoading, famAxes, setFamAxes, famSortCol, handleFamSort, famSortDir, expandedFam, setExpandedFam, processFamily, setProcessFamily, pfCounts, totalParts }) {
  const availableAxes = getAxesForFamily(processFamily)

  const toggleAxis = (key) => {
    setFamAxes(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key)
      return [...prev, key]
    })
    setExpandedFam(new Set())
  }

  const toggleExpand = (idx) => {
    setExpandedFam(prev => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const handleProcessChange = (pf) => {
    setProcessFamily(pf)
    const newAxes = getAxesForFamily(pf)
    setFamAxes(newAxes.slice(0, 2).map(a => a.key))
    setExpandedFam(new Set())
    setFamPage(1)
  }

  return (
    <div className="space-y-4">
      {/* Process family selector */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500 uppercase">Process:</span>
        {PROCESS_FAMILIES.map(pf => (
          <button
            key={pf.key}
            onClick={() => handleProcessChange(pf.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              processFamily === pf.key
                ? 'bg-[#1e5045] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {pf.label}
            {pf.key !== 'all' && pfCounts[pf.key] != null && (
              <span className="ml-1 opacity-70">({pfCounts[pf.key]})</span>
            )}
            {pf.key === 'all' && totalParts > 0 && (
              <span className="ml-1 opacity-70">({totalParts})</span>
            )}
          </button>
        ))}
      </div>

      {/* Axis toggles */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-gray-500 uppercase">Group by:</span>
        {availableAxes.map(ax => (
          <button
            key={ax.key}
            onClick={() => toggleAxis(ax.key)}
            className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
              famAxes.includes(ax.key)
                ? 'bg-[#1e5045]/10 border-[#1e5045] text-[#1e5045]'
                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400'
            }`}
          >
            {ax.label}
          </button>
        ))}
      </div>

      {/* Families table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">
            {famTotal} Part Families
          </span>
          <span className="text-xs text-gray-400">
            grouped by {famAxes.map(a => (availableAxes.find(x => x.key === a) || { label: a }).label).join(' · ')}
          </span>
        </div>
        {famLoading ? (
          <div className="py-12 text-center text-gray-400 text-sm">Loading families...</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="w-8 px-2"></th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Family</th>
                    <SortTh col="part_count" label="Parts" sortCol={famSortCol} sortDir={famSortDir} onSort={handleFamSort} />
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Suppliers</th>
                    <SortTh col="total_spend" label="Spend" sortCol={famSortCol} sortDir={famSortDir} onSort={handleFamSort} />
                  </tr>
                </thead>
                <tbody>
                  {familiesData.map((fam, idx) => {
                    const label = famAxes.map(ax => (fam[ax] || 'unknown').replace(/_/g, ' ')).join(' · ')
                    return (
                      <React.Fragment key={idx}>
                        <tr className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer group" onClick={() => toggleExpand(idx)}>
                          <td className="px-2">
                            <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] border transition-colors ${
                              expandedFam.has(idx) ? 'bg-[#1e5045] text-white border-[#1e5045]' : 'bg-gray-50 text-gray-400 border-gray-200 group-hover:border-gray-400'
                            }`}>
                              {expandedFam.has(idx) ? <FaChevronDown size={8} /> : <FaChevronRight size={8} />}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-medium text-gray-800 capitalize">{label}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{fam.part_count}</td>
                          <td className="px-3 py-2">
                            <span className={`font-medium ${fam.supplier_count >= 5 ? 'text-red-600' : fam.supplier_count >= 3 ? 'text-amber-600' : 'text-green-600'}`}>
                              {fam.supplier_count || 0}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-medium text-[#1e5045]">
                            {fam.total_spend > 0 ? `€${fam.total_spend.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'}
                          </td>
                        </tr>
                        {expandedFam.has(idx) && (
                          <tr>
                            <td colSpan={5} className="p-0">
                              <div className="mx-4 mb-3 ml-9 border-l-[3px] border-[#1e5045] rounded-r-lg bg-gray-50 overflow-hidden">
                                <table className="w-full text-[11px]">
                                  <thead>
                                    <tr className="bg-gray-100">
                                      <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase text-[9px]">Part</th>
                                      <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase text-[9px]">Suppliers</th>
                                      <th className="px-3 py-1.5 text-right font-semibold text-gray-500 uppercase text-[9px]">Spend</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(fam.filenames || []).slice(0, 20).map(fn => (
                                      <tr key={fn} className="border-b border-gray-100 last:border-0 hover:bg-white/50">
                                        <td className="px-3 py-1.5 font-mono font-medium text-gray-700">{fn}</td>
                                        <td className="px-3 py-1.5">
                                          <div className="flex flex-wrap gap-1">
                                            {(fam.suppliers || []).slice(0, 3).map(s => (
                                              <span key={s} className="inline-block px-2 py-0.5 rounded text-[10px] bg-[#1e5045]/10 text-[#1e5045]">
                                                {s.length > 20 ? s.slice(0, 20) + '…' : s}
                                              </span>
                                            ))}
                                          </div>
                                        </td>
                                        <td className="px-3 py-1.5 text-right text-gray-500">—</td>
                                      </tr>
                                    ))}
                                    {(fam.filenames?.length || 0) > 20 && (
                                      <tr><td colSpan={3} className="px-3 py-1.5 text-center text-gray-400 text-[10px]">
                                        + {fam.filenames.length - 20} more parts
                                      </td></tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                  {familiesData.length === 0 && !famLoading && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                        No families found. Ensure drawings have been analyzed.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {famTotalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                <button
                  onClick={() => setFamPage(p => Math.max(1, p - 1))}
                  disabled={famPage === 1}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg disabled:opacity-40 hover:bg-gray-200"
                >
                  <FaChevronLeft size={10} /> Prev
                </button>
                <span className="text-xs text-gray-500">Page {famPage} of {famTotalPages} ({famTotal} families)</span>
                <button
                  onClick={() => setFamPage(p => Math.min(famTotalPages, p + 1))}
                  disabled={famPage === famTotalPages}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg disabled:opacity-40 hover:bg-gray-200"
                >
                  Next <FaChevronRight size={10} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ─── Filter Tooltips (thorough explanations) ─────────────────────────────────
const FILTER_TOOLTIPS = {
  material: `Material Family — The base material classification extracted from the drawing's material callout or title block. Derived by SourceOptima AI from text like "Al 6061-T6" → Aluminum, "SS 304" → Stainless, "Ti-6Al-4V" → Titanium. Grouped into families for sourcing cohort analysis.`,
  process: `Primary Process — The dominant manufacturing process identified from the drawing's geometry, notes, and specifications. Determined by SourceOptima AI analyzing features like thread callouts (machining), bend tables (sheet metal), weld symbols (weldment), or casting notes.`,
  envelope: `Envelope Class — The overall size classification based on the part's bounding box dimensions (L×W×H). S = small (fits in a hand), M = medium (desktop-sized), L = large (requires handling equipment). Computed from extracted dimensions.`,
  tolerance: `Tolerance Band — Categorizes the tightest specified tolerance on the drawing. Ultra (≤0.025mm / ≤0.001"), Precision (≤0.1mm / ≤0.004"), Commercial (>0.1mm). Derived from the tightest non-general linear dimension tolerance or GD&T frame found on the drawing. Drives machine capability and cost.`,
  complexity: `Complexity Score — A 1-5 rating of manufacturing difficulty based on feature count, tolerance requirements, number of setups, and process interactions. Simple (1-2): basic turning or 2D profiles. Moderate (3): multi-setup or tight tolerances. Complex (4-5): 5-axis, tight GD&T, multiple processes.`,
  axis: `Axis Count — The minimum number of CNC axes required to produce the part. Derived from the machining route: Lathe/Swiss = 2-axis, 3-axis mill, 3+2 or mill-turn = 4-axis, simultaneous 5-axis. Only applies to machined parts. Affects which suppliers can quote the work.`,
  setup: `Setup Count — The estimated number of discrete machine setups (re-fixturings) needed. Simple (1 setup): single-side work. Moderate (2): flip part once. Complex (3-4): multiple orientations. Very Complex (5+): requires specialized fixturing. Each setup adds cost and potential tolerance stack-up.`,
  bend: `Bend Class — For sheet metal parts only. Classifies the number of bends: Flat (0 bends, laser/waterjet only), Simple (1-2 bends), Moderate (3-6 bends), Complex (7+ bends). More bends = longer cycle time, tighter formed tolerances needed, and more expensive tooling.`,
  stock_form: `Stock Form — The raw material starting shape before machining. Bar (round/hex stock), Plate (flat stock), Sheet (thin flat), Casting (near-net poured shape), Extrusion (pushed profile), Wire. Affects buy-to-fly ratio and whether near-net-shape sourcing could reduce cost.`,
  btf: `Buy-to-Fly Ratio — Weight of raw material purchased ÷ weight of finished part. Low (≤2:1) = efficient, material well-utilized. Moderate (2-5:1) = normal machining. High (5-10:1) = significant material removal. Extreme (>10:1) = strong candidate for near-net-shape processes like castings or forgings to reduce waste.`,
  cert: `Certification Level — The regulatory/quality certification burden derived from notes, title block requirements, and material specs on the drawing. None = standard commercial. Standard = ISO 9001 or equivalent. Restricted = aerospace (AS9100, NADCAP) or medical (ISO 13485). Limits which suppliers are qualified.`,
  confidence: `Extraction Confidence — How confident SourceOptima AI is in the data extracted from this drawing. High = clear, readable drawing with standard formatting. Medium = some ambiguity or partial data. Low = poor scan quality, non-standard format, or conflicting information.`,
  size: `Size Band — Finer-grained size classification than Envelope Class. Miniature (<50mm), Small (50-150mm), Medium (150-500mm), Large (500-1500mm), Oversize (>1500mm). Based on the largest bounding box dimension. Affects shipping, handling, and machine capacity requirements.`,
  shape: `Shape Class — The overall geometric archetype of the part, classified by SourceOptima AI from the drawing views. Rotational = axially symmetric (discs, shafts, flanges). Prismatic = solid 3D machined blocks. Flat Plate = thin flat panels. Formed Sheet = bent sheet metal. Elongated = high aspect ratio bars/tubes. Weldment = welded structures. Assembly = multi-component.`,
  shape_sub: `Shape Subclass — More specific geometry within the parent shape class. For example: within Rotational you may find discs, rings, cylinders, cones, or shafts. Within Prismatic: blocks, housings, or brackets. Useful for finding similar parts for group-buy or supplier specialization.`,
  drawing_type: `Drawing Type — Classification of the engineering drawing: Detail (single machined/formed part), Assembly (multi-part assembly), Weldment (welded fabrication), Sheet Metal (formed sheet part). Determines quoting approach and supplier type.`,
  coating: `Surface Treatment / Coating — The finish or protective coating specified on the drawing. Examples: Anodize, Nickel Plate, Powder Coat, Cataphoresis, Paint. Affects supplier capability requirements and lead time.`,
  condition_temper: `Material Condition/Temper — The metallurgical state of the material. Examples: T6 (precipitation hardened), H32 (strain hardened), Annealed, Normalized. Affects material sourcing and post-processing requirements.`,
  machining_route: `Machining Route — The CNC strategy identified from the part geometry: Lathe, 3-Axis Mill, Mill-Turn, 5-Axis. Determines machine type and supplier capability needed.`,
  secondary_processes: `Secondary Processes — Additional manufacturing steps beyond the primary process. Examples: Heat Treat, Anodize, Deburr, Passivate, NDT, Grinding. Each adds cost, lead time, and potential supplier sub-contracting.`,
  weight: `Weight Band — Part mass classification: Featherweight (<100g), Light (100-500g), Medium (500g-2kg), Heavy (2-10kg), Very Heavy (>10kg). Affects shipping cost, handling, and machine capacity.`,
  gdt: `GD&T Precision — The tightest geometric tolerance (GD&T frame) on the drawing. Ultra Precision (≤10µm), Precision (≤50µm), Standard (≤100µm), Coarse (>100µm). Drives CMM inspection requirements and cost.`,
  dim_density: `Dimension Density — Number of dimension callouts on the drawing. Sparse (≤5), Moderate (6-15), Dense (16-40), Very Dense (>40). Higher density = more inspection points = higher quality cost.`,
  units: `Drawing Units — Measurement system declared on the drawing: mm (metric) or inch (imperial). Affects which machine shops can easily produce the part without conversion errors.`,
  released_status: `Released Status — Document control state of the drawing. Released = approved for production. Preliminary = still in design review. Affects whether RFQ is appropriate.`,
}

// Display-friendly value labels
const VALUE_LABELS = {
  tol_unspec: 'Unspecified', ultra: 'Ultra (≤25µm)', precision: 'Precision (≤0.1mm)', commercial: 'Commercial (>0.1mm)',
  axis_unspec: 'Unspecified', '2axis': '2-Axis', '3axis': '3-Axis', '4axis': '4-Axis', '5axis': '5-Axis',
  complexity_unspec: 'Unspecified', simple: 'Simple', moderate: 'Moderate', complex: 'Complex',
  setup_unspec: 'Unspecified', setup_simple: 'Simple (1)', setup_moderate: 'Moderate (2)', setup_complex: 'Complex (3-4)', setup_very_complex: 'Very Complex (5+)',
  bends_unspec: 'Unspecified', flat: 'Flat (0)', 'simple': 'Simple (1-2)', 'moderate': 'Moderate (3-6)', 'complex': 'Complex (7+)',
  gauge_unspec: 'Unspecified', thin: 'Thin (≤1mm)', medium: 'Medium (1-2mm)', heavy: 'Heavy (2-4mm)', plate: 'Plate (>4mm)',
  btf_unspec: 'Unspecified', btf_low: 'Low (≤2:1)', btf_moderate: 'Moderate (2-5:1)', btf_high: 'High (5-10:1)', btf_extreme: 'Extreme (>10:1)',
  form_other: 'Other', near_net: 'Near-Net', subtractive: 'Subtractive',
  none: 'None', standard: 'Standard', restricted: 'Restricted', highly_restricted: 'Highly Restricted',
  size_unspec: 'Unspecified', miniature: 'Miniature', small: 'Small', medium: 'Medium', large: 'Large', oversize: 'Oversize',
  machining: 'Machining', sheet_metal: 'Sheet Metal', casting: 'Casting', weldment: 'Weldment', assembly: 'Assembly', other: 'Other',
  high: 'High', low: 'Low',
  // Shape classes
  rotational: 'Rotational', prismatic: 'Prismatic', flat_plate: 'Flat Plate', formed_sheet: 'Formed Sheet',
  elongated: 'Elongated', unknown: 'Unknown',
  // Shape subclasses
  disc: 'Disc', ring: 'Ring', cylinder: 'Cylinder', cone: 'Cone', sphere: 'Sphere', shaft: 'Shaft',
  block: 'Block', housing: 'Housing', bracket: 'Bracket', plate: 'Plate', panel: 'Panel', cover: 'Cover',
  enclosure: 'Enclosure', channel: 'Channel', shield: 'Shield', bar: 'Bar', tube: 'Tube', rod: 'Rod',
  rail: 'Rail', frame: 'Frame', fabrication: 'Fabrication',
  // Drawing types
  detail: 'Detail', assembly: 'Assembly', weldment: 'Weldment', sheet_metal: 'Sheet Metal',
  // Weight bands
  featherweight: 'Featherweight (<100g)', light: 'Light (100-500g)', heavy: 'Heavy (2-10kg)', very_heavy: 'Very Heavy (>10kg)',
  weight_unspec: 'Unspecified',
  // GD&T bands
  ultra_precision: 'Ultra (≤10µm)', precision: 'Precision (≤50µm)', coarse: 'Coarse (>100µm)',
  gdt_unspec: 'Unspecified',
  // Dim density bands
  sparse: 'Sparse (≤5)', moderate: 'Moderate (6-15)', dense: 'Dense (16-40)', very_dense: 'Very Dense (>40)',
  dim_unspec: 'Unspecified',
  // Machining routes
  'mill-turn': 'Mill-Turn', '3-axis': '3-Axis Mill', '5-axis': '5-Axis Mill', lathe: 'Lathe', swiss: 'Swiss',
  // Released status
  released: 'Released', preliminary: 'Preliminary',
}

// Logical sort orders for ordinal facets (values not listed here sort alphabetically)
const SORT_ORDERS = {
  size: ['miniature', 'small', 'medium', 'large', 'oversize', 'size_unspec'],
  tolerance: ['ultra', 'precision', 'commercial', 'tol_unspec'],
  complexity: ['simple', 'moderate', 'complex', 'complexity_unspec'],
  axis: ['2axis', '3axis', '4axis', '5axis', 'axis_unspec'],
  setup: ['setup_simple', 'setup_moderate', 'setup_complex', 'setup_very_complex', 'setup_unspec'],
  bend: ['flat', 'simple', 'moderate', 'complex', 'bends_unspec'],
  btf: ['btf_low', 'btf_moderate', 'btf_high', 'btf_extreme', 'btf_unspec'],
  cert: ['none', 'standard', 'restricted', 'highly_restricted'],
  confidence: ['high', 'medium', 'low'],
  shape: ['rotational', 'prismatic', 'flat_plate', 'formed_sheet', 'elongated', 'weldment', 'assembly', 'unknown'],
  shape_sub: ['disc', 'ring', 'cylinder', 'cone', 'sphere', 'shaft', 'block', 'housing', 'bracket', 'plate', 'panel', 'cover', 'enclosure', 'channel', 'shield', 'bar', 'tube', 'rod', 'rail', 'frame', 'fabrication', 'assembly', 'unknown'],
  drawing_type: ['detail', 'assembly', 'weldment', 'sheet_metal'],
  weight: ['featherweight', 'light', 'medium', 'heavy', 'very_heavy', 'weight_unspec'],
  gdt: ['ultra_precision', 'precision', 'standard', 'coarse', 'gdt_unspec'],
  dim_density: ['sparse', 'moderate', 'dense', 'very_dense', 'dim_unspec'],
  machining_route: ['lathe', 'swiss', '3-axis', 'mill-turn', '5-axis'],
}

function sortFacetItems(key, items) {
  if (!items) return []
  const order = SORT_ORDERS[key]
  if (order) {
    // Sort by defined logical order; unknown values go to end
    return [...items].sort((a, b) => {
      const ai = order.indexOf(a.value)
      const bi = order.indexOf(b.value)
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
    })
  }
  // Alphabetical sort by display label
  return [...items].sort((a, b) => getDisplayLabel(a.value).localeCompare(getDisplayLabel(b.value)))
}

function getDisplayLabel(val) {
  return VALUE_LABELS[val] || val
}

// ─── Tooltip component ────────────────────────────────────────────────────────
function InfoTooltip({ text }) {
  const [show, setShow] = useState(false)
  return (
    <span className="relative inline-block">
      <FaInfoCircle
        size={10}
        className="text-gray-300 hover:text-[#1e5045] cursor-help transition-colors"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      />
      {show && (
        <div className="absolute bottom-full left-0 mb-2 w-72 p-3 bg-white text-gray-700 text-[11px] leading-relaxed rounded-xl shadow-lg border border-gray-200 z-50 pointer-events-none">
          {text}
          <div className="absolute top-full left-4 border-4 border-transparent border-t-white" />
        </div>
      )}
    </span>
  )
}

// ─── Collapsible Section ──────────────────────────────────────────────────────
function FilterSection({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-2 text-[10px] font-bold text-gray-500 uppercase tracking-wider hover:text-gray-700"
      >
        {title}
        <FaChevronDown size={8} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="pb-3 space-y-3">{children}</div>}
    </div>
  )
}

// ─── FacetGroup with counts and tooltip ───────────────────────────────────────
function FacetGroupNew({ label, tooltip, items, active, onToggle, maxVisible = 6 }) {
  const [expanded, setExpanded] = useState(false)
  if (!items || items.length === 0) return null

  const visible = expanded ? items : items.slice(0, maxVisible)
  const hasMore = items.length > maxVisible

  return (
    <div>
      <div className="flex items-center gap-1 mb-1.5">
        <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">{label}</span>
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
      <div className="space-y-0.5 max-h-40 overflow-y-auto">
        {visible.map(item => (
          <label key={item.value} className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer hover:text-gray-800 py-0.5 px-1 rounded hover:bg-gray-50">
            <input
              type="checkbox"
              checked={active.includes(item.value)}
              onChange={() => onToggle(item.value)}
              className="w-3 h-3 rounded border-gray-300 text-[#1e5045] focus:ring-[#1e5045]"
            />
            <span className="flex-1 truncate">{getDisplayLabel(item.value)}</span>
            <span className="text-[10px] text-gray-400 tabular-nums">{item.count}</span>
          </label>
        ))}
      </div>
      {hasMore && (
        <button onClick={() => setExpanded(!expanded)} className="text-[10px] text-[#1e5045] hover:underline mt-1 ml-1">
          {expanded ? 'Show less' : `+ ${items.length - maxVisible} more`}
        </button>
      )}
    </div>
  )
}

// ─── Full Filter Rail ─────────────────────────────────────────────────────────
function FilterRail({ facetCounts, filters, toggleFilterValue, activeCount, clearAll, onResetAll }) {
  const fc = facetCounts || {}

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
          <FaFilter size={12} className="text-[#1e5045]" />
          Filters
          {activeCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-[#f26522] text-white text-[10px] rounded-full">{activeCount}</span>
          )}
        </div>
        {activeCount > 0 && (
          <button onClick={clearAll} className="text-[10px] text-gray-400 hover:text-red-500 flex items-center gap-0.5">
            <FaTimes size={9} /> Clear
          </button>
        )}
      </div>

      {/* Drawing Classification */}
      <FilterSection title="Drawing" defaultOpen={true}>
        <FacetGroupNew label="Drawing Type" tooltip={FILTER_TOOLTIPS.drawing_type} items={sortFacetItems('drawing_type', fc.drawing_type)} active={filters.drawing_type} onToggle={v => toggleFilterValue('drawing_type', v)} />
        <FacetGroupNew label="Units" tooltip={FILTER_TOOLTIPS.units} items={sortFacetItems('units', fc.units)} active={filters.units} onToggle={v => toggleFilterValue('units', v)} />
        <FacetGroupNew label="Released Status" tooltip={FILTER_TOOLTIPS.released_status} items={sortFacetItems('released_status', fc.released_status)} active={filters.released_status} onToggle={v => toggleFilterValue('released_status', v)} />
      </FilterSection>

      {/* Engineering Section */}
      <FilterSection title="Engineering" defaultOpen={true}>
        <FacetGroupNew label="Material" tooltip={FILTER_TOOLTIPS.material} items={sortFacetItems('material', fc.material)} active={filters.material} onToggle={v => toggleFilterValue('material', v)} />
        <FacetGroupNew label="Condition/Temper" tooltip={FILTER_TOOLTIPS.condition_temper} items={sortFacetItems('condition_temper', fc.condition_temper)} active={filters.condition_temper} onToggle={v => toggleFilterValue('condition_temper', v)} />
        <FacetGroupNew label="Coating" tooltip={FILTER_TOOLTIPS.coating} items={sortFacetItems('coating', fc.coating)} active={filters.coating} onToggle={v => toggleFilterValue('coating', v)} />
        <FacetGroupNew label="Process" tooltip={FILTER_TOOLTIPS.process} items={sortFacetItems('process', fc.process)} active={filters.process} onToggle={v => toggleFilterValue('process', v)} />
        <FacetGroupNew label="Machining Route" tooltip={FILTER_TOOLTIPS.machining_route} items={sortFacetItems('machining_route', fc.machining_route)} active={filters.machining_route} onToggle={v => toggleFilterValue('machining_route', v)} />
        <FacetGroupNew label="Secondary Processes" tooltip={FILTER_TOOLTIPS.secondary_processes} items={sortFacetItems('secondary_processes', fc.secondary_processes)} active={filters.secondary_processes} onToggle={v => toggleFilterValue('secondary_processes', v)} />
        <FacetGroupNew label="Size" tooltip={FILTER_TOOLTIPS.size} items={sortFacetItems('size', fc.size)} active={filters.size} onToggle={v => toggleFilterValue('size', v)} />
        <FacetGroupNew label="Weight" tooltip={FILTER_TOOLTIPS.weight} items={sortFacetItems('weight', fc.weight)} active={filters.weight} onToggle={v => toggleFilterValue('weight', v)} />
      </FilterSection>

      {/* Shape Section */}
      <FilterSection title="Shape" defaultOpen={true}>
        <FacetGroupNew label="Shape Class" tooltip={FILTER_TOOLTIPS.shape} items={sortFacetItems('shape', fc.shape)} active={filters.shape} onToggle={v => toggleFilterValue('shape', v)} />
        <FacetGroupNew label="Shape Detail" tooltip={FILTER_TOOLTIPS.shape_sub} items={sortFacetItems('shape_sub', fc.shape_sub)} active={filters.shape_sub} onToggle={v => toggleFilterValue('shape_sub', v)} />
      </FilterSection>

      {/* Manufacturing Section */}
      <FilterSection title="Manufacturing" defaultOpen={true}>
        <FacetGroupNew label="Tolerance" tooltip={FILTER_TOOLTIPS.tolerance} items={sortFacetItems('tolerance', fc.tolerance)} active={filters.tolerance} onToggle={v => toggleFilterValue('tolerance', v)} />
        <FacetGroupNew label="GD&T Precision" tooltip={FILTER_TOOLTIPS.gdt} items={sortFacetItems('gdt', fc.gdt)} active={filters.gdt} onToggle={v => toggleFilterValue('gdt', v)} />
        <FacetGroupNew label="Dim Density" tooltip={FILTER_TOOLTIPS.dim_density} items={sortFacetItems('dim_density', fc.dim_density)} active={filters.dim_density} onToggle={v => toggleFilterValue('dim_density', v)} />
        <FacetGroupNew label="Complexity" tooltip={FILTER_TOOLTIPS.complexity} items={sortFacetItems('complexity', fc.complexity)} active={filters.complexity} onToggle={v => toggleFilterValue('complexity', v)} />
        <FacetGroupNew label="Axis Count" tooltip={FILTER_TOOLTIPS.axis} items={sortFacetItems('axis', fc.axis)} active={filters.axis} onToggle={v => toggleFilterValue('axis', v)} />
        <FacetGroupNew label="Setup Count" tooltip={FILTER_TOOLTIPS.setup} items={sortFacetItems('setup', fc.setup)} active={filters.setup} onToggle={v => toggleFilterValue('setup', v)} />
        <FacetGroupNew label="Bend Class" tooltip={FILTER_TOOLTIPS.bend} items={sortFacetItems('bend', fc.bend)} active={filters.bend} onToggle={v => toggleFilterValue('bend', v)} />
      </FilterSection>

      {/* Sourcing Section */}
      <FilterSection title="Sourcing" defaultOpen={true}>
        <FacetGroupNew label="Stock Form" tooltip={FILTER_TOOLTIPS.stock_form} items={sortFacetItems('stock_form', fc.stock_form)} active={filters.stock_form} onToggle={v => toggleFilterValue('stock_form', v)} />
        <FacetGroupNew label="Buy-to-Fly" tooltip={FILTER_TOOLTIPS.btf} items={sortFacetItems('btf', fc.btf)} active={filters.btf} onToggle={v => toggleFilterValue('btf', v)} />
        <FacetGroupNew label="Certification" tooltip={FILTER_TOOLTIPS.cert} items={sortFacetItems('cert', fc.cert)} active={filters.cert} onToggle={v => toggleFilterValue('cert', v)} />
      </FilterSection>

      {/* Confidence Section */}
      <FilterSection title="Confidence" defaultOpen={true}>
        <FacetGroupNew label="Confidence" tooltip={FILTER_TOOLTIPS.confidence} items={sortFacetItems('confidence', fc.confidence)} active={filters.confidence} onToggle={v => toggleFilterValue('confidence', v)} maxVisible={5} />
      </FilterSection>

      {/* Reset All */}
      <div className="pt-3 mt-2 border-t border-gray-100">
        <button
          onClick={() => { clearAll(); if (onResetAll) onResetAll() }}
          className="w-full text-xs text-gray-500 hover:text-[#1e5045] hover:bg-gray-50 rounded-lg py-2 transition-colors flex items-center justify-center gap-1.5"
        >
          <FaUndo size={10} />
          Reset All Filters
        </button>
      </div>
    </div>
  )
}

// PROCESS_FAMILIES used by FamiliesPanel below
